import { useState } from "react";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import BTGSuccessMessage from "@features/newsletter/components/BTGSuccessMessage";
import NewsletterForm from "@features/newsletter/components/NewsletterForm";
import { Transition } from "@headlessui/react";
import { useWindowSize } from "@hooks/useWindowSize";
import { cn } from "@util/helpers";

type ASEANNewsletterProps = {
  /** The classname of the component. */
  className?: string;
};

/**
 * BT Global Newsletter component to subscribe to global newsletter which is use in /global page
 * Reference:
 *   - transition classes: https://www.skies.dev/headless-ui-transitions
 * */
const ASEANNewsletter = ({
  className,
}: ASEANNewsletterProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();
  const sendDataToGTM = useGTMDispatch();
  const [isShowForm, setIsShowForm] = useState(false);

  const newsletter = BT_NEWSLETTERS.find(
    (newsletter) => newsletter.type === NEWSLETTER_TYPE.ASEAN
  );

  if (typeof newsletter === "undefined") return <></>;

  const transitionClasses = {
    enter: "transition ease-linear duration-200",
    enterFrom: "opacity-0 -translate-y-1",
    enterTo: "opacity-100 translate-y-0",
    leave: "transition ease-in duration-150",
    leaveFrom: "opacity-100 translate-y-0",
    leaveTo: "opacity-0 -translate-y-1",
  };

  const handleShowForm = () => {
    setIsShowForm(true);
  };

  return (
    <div
      data-testid="bt-global-newsletter-component"
      data-section-name="newsletter"
      data-section-label="asean"
      className={cn(
        "track-impression mx-auto flex w-full flex-col items-center justify-between gap-4 pb-8 pt-6",
        "lg:w-9/12 lg:flex-row lg:gap-6 lg:border-none",
        "xl:w-8/12 xl:px-7",
        className
      )}
    >
      <div className="flex items-center justify-center gap-6">
        <img
          className="relative z-10"
          src={newsletter.thumbnail}
          width={75}
          height={75}
          alt=""
        />

        <div>
          {isScreenLG ? (
            <h4 className="font-freightSansPro text-xl font-bold leading-7 text-verticals-btblue">
              {newsletter.label}
            </h4>
          ) : null}

          <p className="font-poppins text-xs font-medium leading-normal text-gray-750">
            {!isScreenLG ? newsletter.label + ": " : null}
            {newsletter.description}{" "}
            {!isScreenLG ? (
              <span
                className="font-poppins text-xs font-medium leading-normal text-verticals-btblue underline"
                onClick={handleShowForm}
              >
                {"Signup »"}
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <Transition
        show={isScreenLG || isShowForm}
        as="div"
        className="w-full lg:w-auto"
        {...transitionClasses}
      >
        <NewsletterForm
          className="group w-full transition duration-300 ease-in data-[closed]:opacity-0 lg:w-auto lg:min-w-[290px]"
          buttonClassName={cn(
            "px-2 py-1 flex items-center justify-center text-verticals-btblue hover:opacity-75 border border-blue-50 w-auto"
          )}
          formClassName={cn("py-2 border-b border-gray-350")}
          inputClassName={cn(
            "rounded-full p-0 placeholder:text-blue-170 text-sm"
          )}
          newsletters={[newsletter]}
          isBordered={false}
          placeholder="Your email address"
          signUpButtonIcon={
            <IconFactory
              type={IconTypeEnum.rightArrow}
              className="text-verticals-btblue"
            />
          }
          onSubmitCallback={() => {
            sendDataToGTM({
              event: "custom_event",
              eventCategory: "newsletter",
              eventAction: "click",
              eventLabel: "asean business",
            });
          }}
          onSuccessCallback={() => {
            sendDataToGTM({
              event: "custom_event",
              eventCategory: "newsletter",
              eventAction: "signup",
              eventLabel: "asean business",
            });
          }}
          successMessage={<BTGSuccessMessage className="text-xs" />}
        />
      </Transition>
    </div>
  );
};

export default ASEANNewsletter;
